import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { X, Monitor } from 'lucide-react';

const hotspots = [
  { 
    id: 1, 
    x: 72, 
    y: 58, 
    title: "Gen 7 Spintronic Reader", 
    description: "Ensures data is read accurately and swiftly at ultra-high areal densities Leveraging this phenomenon allows for high-resolution magnetic readback, which is crucial for accurately reading smaller, densely packed bits.", 
    image: "./C.jpg" 
  },
  { 
    id: 2, 
    x: 55, 
    y: 90, 
    title: "Plasmonic Writer", 
    description: "At the core of this writer's operation is the ability to precisely heat the superlattice platinum-alloy media to over 800º Fahrenheit, which then cools again in less than two nanoseconds.", 
    image: "./F.jpg" 
  },
  { 
    id: 3, 
    x: 58, 
    y: 80, 
    title: "Quantum Antenna", 
    description: "Converts the laser's photonic energy into a plasmonic state to enable recording.", 
    image: "./E.jpg" 
  },
  { 
    id: 4, 
    x: 58, 
    y: 60, 
    title: "Photonic Funnel", 
    description: "Channels the laser light from its source to the quantum antenna.", 
    image: "./B.jpg" 
  },
  { 
    id: 5, 
    x: 58, 
    y: 37, 
    title: "Nanophotonic Laser", 
    description: "A precise light beam momentarily heats the recording media.", 
    image: "./G.jpg" 
  },
  { 
    id: 6, 
    x: 20, 
    y: 85, 
    title: "Superlattice Platinum-Alloy Media", 
    description: "Each nanoparticle in the superlattice platinum-alloy media acts as a bit of data, stabilized by the media's high magnetic anisotropy and unique magnetic alloys.", 
    image: "./A.jpg" 
  },
  { 
    id: 7, 
    x: 35, 
    y: 25, 
    title: "12nm Integrated Controller", 
    description: "Seagate's 12nm integrated controller unleashes a torrent of real-time processing power in a smaller footprint optimized for energy savings.", 
    image: "./D.jpg" 
  }
];

const InteractiveHotspotImage = () => {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [infoBoxPosition, setInfoBoxPosition] = useState({ left: 0, top: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const infoBoxRef = useRef(null);

  // Check for mobile/tablet and orientation
  useEffect(() => {
    const checkSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsMobile(width < 768);
      setIsLandscape(width > height && width < 1024); // Landscape phones/small tablets
    };
    checkSize();
    window.addEventListener('resize', checkSize);
    window.addEventListener('orientationchange', checkSize);
    return () => {
      window.removeEventListener('resize', checkSize);
      window.removeEventListener('orientationchange', checkSize);
    };
  }, []);

  // Track image dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (imageRef.current && containerRef.current) {
        const imgRect = imageRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        setImageDimensions({
          width: imgRect.width,
          height: imgRect.height,
          offsetX: (containerRect.width - imgRect.width) / 2,
          offsetY: (containerRect.height - imgRect.height) / 2
        });
      }
    };
    if (imageLoaded) updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [imageLoaded]);

  // Desktop: Position info box next to dot
  useLayoutEffect(() => {
    if (!isMobile && !isLandscape && activeHotspot && infoBoxRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const boxRect = infoBoxRef.current.getBoundingClientRect();
      
      const dotX = imageDimensions.offsetX + (activeHotspot.x / 100) * imageDimensions.width;
      const dotY = imageDimensions.offsetY + (activeHotspot.y / 100) * imageDimensions.height;
      
      const gap = 50;
      const padding = 20;

      let left = dotX + gap;
      if (left + boxRect.width > containerRect.width - padding) {
        left = dotX - boxRect.width - gap;
      }

      let top = dotY - (boxRect.height / 2);
      if (top < padding) top = padding;
      if (top + boxRect.height > containerRect.height - padding) {
        top = containerRect.height - boxRect.height - padding;
      }

      setInfoBoxPosition({ left, top });
    }
  }, [activeHotspot, imageDimensions, isMobile, isLandscape]);

  const handleHotspotClick = (hotspot) => {
    setActiveHotspot(activeHotspot?.id === hotspot.id ? null : hotspot);
  };

  // Determine if we should show bottom sheet (mobile portrait OR landscape)
  const showBottomSheet = isMobile || isLandscape;

  return (
    <div 
      ref={containerRef} 
      className="w-screen h-screen bg-black overflow-hidden flex items-center justify-center relative font-sans"
    >
      {/* Background Image */}
      <img 
        ref={imageRef} 
        src="./base-image.jpg" 
        alt="Interactive Technology Diagram"
        className="max-w-full max-h-full object-contain pointer-events-none" 
        onLoad={() => setImageLoaded(true)} 
      />
      
      {/* Hotspot Dots */}
      {imageLoaded && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div 
            className="relative pointer-events-auto" 
            style={{ width: imageDimensions.width, height: imageDimensions.height }}
          >
            {hotspots.map((hotspot) => (
              <button
                key={hotspot.id}
                onClick={() => handleHotspotClick(hotspot)}
                className="absolute group flex items-center justify-center z-20"
                style={{ 
                  left: `${hotspot.x}%`, 
                  top: `${hotspot.y}%`, 
                  transform: 'translate(-50%, -50%)' 
                }}
              >
                {/* Pulsing rings */}
                <div className="absolute w-16 h-16 md:w-20 md:h-20 rounded-full border-[3px] md:border-[4px] border-cyan-100/40 animate-[ping_3.5s_ease-out_infinite]" />
                <div className="absolute w-10 h-10 md:w-12 md:h-12 rounded-full border border-cyan-400/30 animate-[ping_4s_ease-in-out_infinite]" />
                
                {/* Center dot */}
                <div 
                  className={`relative w-5 h-5 md:w-7 md:h-7 rounded-full transition-all duration-300 ${
                    activeHotspot?.id === hotspot.id 
                      ? 'bg-cyan-400 border-4 border-white scale-110' 
                      : 'bg-white border-4 border-cyan-400'
                  }`} 
                />
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Info Box - Desktop Only (Side Panel) */}
      {!showBottomSheet && activeHotspot && (
        <div 
          ref={infoBoxRef}
          className="fixed bg-black border border-cyan-500 rounded-2xl shadow-2xl p-6 w-80 z-50 transition-opacity duration-300 max-h-[90vh] overflow-y-auto"
          style={{ 
            left: `${infoBoxPosition.left}px`, 
            top: `${infoBoxPosition.top}px`,
            opacity: infoBoxPosition.left === 0 ? 0 : 1
          }}
        >
          <button 
            onClick={() => setActiveHotspot(null)} 
            className="absolute top-4 right-4 text-gray-400 hover:text-cyan-400 transition-colors"
          >
            <X size={20} />
          </button>
          
          <h3 className="text-cyan-400 text-xl font-bold mb-3 pr-6 leading-tight border-b border-white/10 pb-2">
            {activeHotspot.title}
          </h3>
          
          {activeHotspot.image && (
            <div className="mb-4 rounded-xl overflow-hidden border border-white/5">
              <img 
                src={activeHotspot.image} 
                alt={activeHotspot.title} 
                className="w-full h-36 object-cover" 
              />
            </div>
          )}
          
          <p className="text-white text-sm leading-relaxed font-light">
            {activeHotspot.description}
          </p>
        </div>
      )}

      {/* Info Box - Mobile & Landscape (Bottom Sheet) */}
      {showBottomSheet && activeHotspot && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={() => setActiveHotspot(null)}
          />
          
          {/* Bottom Sheet - for landscape */}
          <div 
            className={`fixed bottom-0 left-0 right-0 bg-gradient-to-b from-gray-900 to-black border-t-2 border-cyan-500 rounded-t-3xl shadow-2xl z-50 overflow-y-auto ${
              isLandscape ? 'max-h-[85vh]' : 'max-h-[70vh]'
            }`}
            style={{
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            <div className="p-6">
              <button 
                onClick={() => setActiveHotspot(null)} 
                className="absolute top-6 right-6 text-gray-400 hover:text-cyan-400 transition-colors z-10"
              >
                <X size={24} />
              </button>
              
              <h3 className="text-cyan-400 text-2xl font-bold mb-4 pr-10 leading-tight">
                {activeHotspot.title}
              </h3>
              
              {activeHotspot.image && (
                <div className="mb-4 rounded-xl overflow-hidden border border-cyan-500/30">
                  <img 
                    src={activeHotspot.image} 
                    alt={activeHotspot.title} 
                    className={`w-full  ${isLandscape ? 'h-32 object-contain' : 'h-48 object-cover'}`}
                  />
                </div>
              )}
              
              <p className="text-gray-300 text-base leading-relaxed">
                {activeHotspot.description}
              </p>
            </div>
          </div>
        </>
      )}

      {/* keyframe animation for slide up */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default InteractiveHotspotImage;